import React, { useState, useEffect } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { LeadSummaryStyles } from "../../styles/pages/Reports/LeadSummaryStyles";
import Loader from "../../components/common/Loader";

interface SummaryItem {
  [key: string]: string | number;
}

const LeadSummary = () => {
  const [leadSourcesData, setLeadSourcesData] = useState<SummaryItem[]>([]);
  const [leadStagesData, setLeadStagesData] = useState<SummaryItem[]>([]);
  const [leadStatusesData, setLeadStatusesData] = useState<SummaryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      const token = sessionStorage.getItem("access_token");
      if (!token) {
        setError("No token found in sessionStorage");
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/reports/lead-summary`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const { data } = response.data;
        setLeadSourcesData(
          (data.lead_source_summary || []).map((item: any) => ({
            source: item.source_name || "N/A",
            count: item.total || "N/A",
          }))
        );
        setLeadStagesData(
          (data.lead_stage_summary || []).map((item: any) => ({
            stage: item.stage_name || "N/A",
            count: item.total || "N/A",
          }))
        );
        setLeadStatusesData(
          (data.lead_status_summary || []).map((item: any) => ({
            status: item.status_name || "N/A",
            count: item.total || "N/A",
          }))
        );
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch data");
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading)
    return (
      <div className={LeadSummaryStyles.loading}>
        <Loader />
      </div>
    );
  if (error) return <div className={LeadSummaryStyles.error}>{error}</div>;

  const renderTable = (
    title: string,
    data: SummaryItem[],
    keyName: string,
    valueKey: string
  ) => (
    <div className={LeadSummaryStyles.tableContainer}>
      <div className={LeadSummaryStyles.titleContainer}>
        <h2 className={LeadSummaryStyles.title}>{title}</h2>
      </div>
      <table className={LeadSummaryStyles.table}>
        <thead className={LeadSummaryStyles.thead}>
          <tr>
            <th className={LeadSummaryStyles.th}>
              {title === "Lead Source" ? "Source" : title.split(" ")[1]}
            </th>
            <th className={LeadSummaryStyles.th}>Total</th>
          </tr>
        </thead>
        <tbody>
          {data.map((item: SummaryItem, index: number) => (
            <tr key={index}>
              <td className={LeadSummaryStyles.linkedTd}>
                <Link
                  to="/lead-summary-details"
                  state={{
                    type:
                      title === "Lead Source"
                        ? "source"
                        : title === "Lead Stage"
                        ? "stage"
                        : "status",
                    value: item[keyName].toString(),
                  }}
                  className="text-blue-600 hover:underline"
                >
                  {item[keyName]}
                </Link>
              </td>
              <td className={LeadSummaryStyles.linkedTd}>{item[valueKey]}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <a href="/lead-list" className={LeadSummaryStyles.viewMore}>
        View More
      </a>
    </div>
  );

  return (
    <div className={LeadSummaryStyles.container}>
      {renderTable("Lead Source", leadSourcesData, "source", "count")}
      {renderTable("Lead Stage", leadStagesData, "stage", "count")}
      {renderTable("Lead Status", leadStatusesData, "status", "count")}
    </div>
  );
};

export default LeadSummary;
